import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { DataTable } from "ui-components/data-table";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import {
  useDockEmployees,
  useDockScanMutations,
  useDockTags,
} from "../hooks/useDockScanQueries";
import { DOCK_TAGS_COLUMN_OPTIONS, getDockTagsColumns } from "../dockTagsColumns";

interface DockTagsTabProps {
  terminalNumber: number;
}

const tagSchema = Yup.object({
  name: Yup.string().trim().required("Tag name is required").max(64),
});

const DockTagsTab: React.FC<DockTagsTabProps> = ({ terminalNumber }) => {
  const { data: tags, isLoading, isError, error } = useDockTags(terminalNumber);
  const {
    data: employeesData,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasMoreEmployees,
    isFetchingNextPage: isFetchingMoreEmployees,
  } = useDockEmployees(terminalNumber);
  const { createTag, deleteTag } = useDockScanMutations(terminalNumber);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    if (hasMoreEmployees && !isFetchingMoreEmployees) {
      void fetchNextEmployeesPage();
    }
  }, [hasMoreEmployees, isFetchingMoreEmployees, fetchNextEmployeesPage]);

  const employees = useMemo(
    () =>
      employeesData?.pages.flatMap((page) =>
        Array.isArray(page) ? page : page?.items ?? []
      ) ?? [],
    [employeesData]
  );

  const tagRows = useMemo(() => {
    const countByTagId: Record<string, number> = {};
    employees.forEach((emp) => {
      (emp.tags || []).forEach((tag) => {
        countByTagId[tag.id] = (countByTagId[tag.id] || 0) + 1;
      });
    });
    return (tags || []).map((tag) => ({
      ...tag,
      employeeCount: countByTagId[tag.id] || 0,
    }));
  }, [tags, employees]);

  const columns = useMemo(
    () =>
      getDockTagsColumns({
        deleteConfirmId,
        isDeleting: deleteTag.isLoading,
        onRequestDelete: (tagId) => setDeleteConfirmId(tagId),
        onConfirmDelete: async (tagId) => {
          setMutationError(null);
          try {
            await deleteTag.mutateAsync(tagId);
            setDeleteConfirmId(null);
          } catch (e) {
            setMutationError((e as Error).message);
          }
        },
        onCancelDelete: () => setDeleteConfirmId(null),
      }),
    [deleteConfirmId, deleteTag]
  );

  return (
    <div className="dock-users__tab-panel">
      <div className="dock-users__tags-create">
        <Formik
          initialValues={{ name: "" }}
          validationSchema={tagSchema}
          onSubmit={async (values, { resetForm }) => {
            setMutationError(null);
            try {
              await createTag.mutateAsync({ terminalNumber, name: values.name.trim() });
              resetForm();
            } catch (e) {
              setMutationError((e as Error).message);
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, dirty, isSubmitting }) => (
            <Form className="dock-users__tags-form">
              <div className="dock-users__filter-field dock-users__filter-field--search">
                <LabeledTextInput
                  name="name"
                  labelMode={LabelModes.column}
                  label="New Tag"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="e.g. First shift"
                  error={touched.name && errors.name ? errors.name : undefined}
                />
              </div>
              <Button
                type="submit"
                theme={ButtonThemes.blue}
                size={ButtonSizes.medium}
                disabled={!dirty || isSubmitting || createTag.isLoading}
              >
                Add Tag
              </Button>
            </Form>
          )}
        </Formik>
      </div>

      {mutationError && <XGSErrorMessage>{mutationError}</XGSErrorMessage>}
      {isLoading && <Loading isLoading />}
      {isError && (
        <XGSErrorMessage>{(error as Error)?.message || "Unable to load tags."}</XGSErrorMessage>
      )}

      {!isLoading && !isError && (
        <DataTable
          columns={columns}
          data={tagRows}
          rowHeight={56}
          responsive
          stickyHeaders
          columnSelectorOptions={DOCK_TAGS_COLUMN_OPTIONS}
          showColumnSelector
          listHeightOverride={Math.min(480, Math.max(160, tagRows.length * 56 + 48))}
        />
      )}
      {!isLoading && !isError && tagRows.length === 0 && (
        <div className="dock-users__empty">No tags defined for this terminal.</div>
      )}
    </div>
  );
};

export default DockTagsTab;
